import { Testimonial } from '~/components/ui/Testimonial'
import { createFileRoute, Link, useHydrated } from '@tanstack/react-router'
import { Pagination } from '~/components/ui/Pagination'
import { YouTubeEmbed } from '~/components/ui/YouTubeEmbed'
import { formatDate } from '~/lib/date'
import { getOpenRescues } from '~/lib/content/content'
import { CONTENT_ATTRIBUTE } from '~/lib/content/links'
import { parseYoutubeUrl } from '~/lib/media/youtube'
import { rescueClass } from '~/components/ui/classes'
import { pageMeta } from '~/lib/site/seo'
import { cacheHeaders } from '~/lib/site/cache'
import { Button, buttonClass } from '~/components/ui/Button'
import { bodyClass } from '~/components/ui/BodyText'
import { Container } from '~/components/ui/Container'
import { DonateSignupBand } from '~/components/sections/DonateSignupBand'
import { PageTitle } from '~/components/ui/PageTitle'
import { PageHero } from '~/components/ui/PageHero'

const TITLE = 'Open Rescue | Direct Action Everywhere'
const DESCRIPTION =
  'Some of the most important actions in the animal rights movement have been rescues on the frontline of animal abuse. Our open rescues are seen by millions.'

const PAGE_SIZE = 10

export const Route = createFileRoute('/open-rescue')({
  // /open-rescue?page=N. Only page 1 is prerendered (the static host has a
  // single /open-rescue/index.html); other pages are sliced client-side from
  // the loader data after hydration.
  validateSearch: (search: Record<string, unknown>): { page?: number } => ({
    page: Number(search.page) > 1 ? Math.floor(Number(search.page)) : undefined,
  }),
  loader: () => getOpenRescues(),
  headers: () => cacheHeaders.content,
  head: () => pageMeta({ title: TITLE, description: DESCRIPTION }),
  component: OpenRescue,
})

function OpenRescue() {
  const hydrated = useHydrated()
  const rescues = Route.useLoaderData()
  const search = Route.useSearch()
  const totalPages = Math.max(1, Math.ceil(rescues.length / PAGE_SIZE))
  const page = hydrated ? Math.min(search.page ?? 1, totalPages) : 1
  const pageItems = rescues.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
  return (
    <>
      <PageHero>
        <Container width="narrow">
          <PageTitle size="md" className="mb-5">
            Open Rescue
          </PageTitle>
          <Testimonial inline attribution="Justin Marceau, professor of law">
            “DxE’s investigations have been among the most important in the
            history of animal protection.”
          </Testimonial>
          <p className={`${bodyClass} mb-5`}>
            Some of the most important actions in the animal rights movement
            have been rescues on the frontline of animal abuse. Our open rescues
            are seen by millions and force the issue of animal rights into the
            public consciousness. DxE activists are now going to court for
            rescues at some of the largest factory farms in the world.
          </p>
          <p className={`${bodyClass} mb-5`}>
            You can learn more about these groundbreaking court cases by
            following the #RightToRescue campaign site.
          </p>
          <Button
            href="https://righttorescue.com/"
            target="_blank"
            rel="noreferrer"
            className="min-w-[200px]"
          >
            Visit RightToRescue.com
          </Button>
        </Container>
      </PageHero>

      <section className="pt-16">
        <Container width="narrow">
          <ul
            id="rescue-list"
            className="m-0 flex scroll-mt-24 list-none flex-col gap-14 p-0 md:gap-16"
          >
            {pageItems.map((r) => {
              const youtube = parseYoutubeUrl(r.video)?.id
              const href = r.href ?? (youtube ? undefined : r.video)
              return (
                <li key={r.slug} className="flex flex-col">
                  <h2 className={rescueClass.title}>{r.title}</h2>
                  <p className={rescueClass.date}>{formatDate(r.date)}</p>
                  <div className="pt-5">
                    <div className={rescueClass.media}>
                      {youtube ? (
                        <YouTubeEmbed id={youtube} title={r.title} />
                      ) : r.image ? (
                        <img
                          {...r.image}
                          sizes="(min-width: 1044px) 940px, 90vw"
                          alt={r.title}
                          loading="lazy"
                          decoding="async"
                          className="aspect-video size-full object-cover"
                        />
                      ) : (
                        <div className="aspect-video bg-surface-alt" />
                      )}
                    </div>
                  </div>
                  {r.html && (
                    <div
                      className={rescueClass.body}
                      {...CONTENT_ATTRIBUTE}
                      dangerouslySetInnerHTML={{ __html: r.html }}
                    />
                  )}
                  {href && (
                    <div className="pt-5">
                      <Button
                        href={href}
                        target="_blank"
                        rel="noreferrer"
                        variant="outline"
                        className="min-w-[150px]"
                      >
                        {r.cta ?? 'Learn more'}
                      </Button>
                    </div>
                  )}
                </li>
              )
            })}
          </ul>
          <Pagination
            className="mt-10"
            page={page}
            totalPages={totalPages}
            total={rescues.length}
            pageSize={PAGE_SIZE}
            pageLink={(p) => ({
              to: '/open-rescue',
              search: { page: p > 1 ? p : undefined },
            })}
            scrollToId="rescue-list"
          />
        </Container>
      </section>

      <section className="py-20 text-center">
        <Container>
          <p className={`${bodyClass} mb-5`}>
            Want to expose industry misconduct? We can help you.
          </p>
          <Link to="/whistleblower-support" className={buttonClass('outline')}>
            Whistleblower support
          </Link>
        </Container>
      </section>

      <DonateSignupBand />
    </>
  )
}
